import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MissionBriefing, MissionResults } from '../../components/mission';
import { useProgress } from '../../context/ProgressContext';
import { getMission } from '../../services/missionService';
import styles from './MissionPage.module.css';
import CoordinateChallenge from '../../components/chess/CoordinateChallenge';
import MoveChallenge from '../../components/chess/MoveChallenge';
import MissionDebugProvider from '../../components/debug/MissionDebugProvider';
import MissionDebugWrapper from '../../components/debug/MissionDebugWrapper';
import {
  createCoordinateCompletionData,
  createMoveCompletionData
} from '../../services/missionCompletionService';
import { useScrollToTopOnChange } from '../../hooks/useScrollToTopOnChange';


// Enum for the different mission phases
enum MissionPhase {
  BRIEFING = 'briefing',
  IN_PROGRESS = 'in_progress',
  RESULTS = 'results'
}

/**
 * Component that displays a mission with its different phases:
 * briefing, active mission, and results
 */
const MissionPage: React.FC = () => {
  const SUCCESS_TRANSITION_DELAY_MS = process.env.NODE_ENV === 'test' ? 0 : 1200;
  // Router hooks for navigation and params
  const { missionId } = useParams<{ missionId: string }>();
  const navigate = useNavigate();
  
  // Context for user progress
  const { userProgress, completeMission, isLoading } = useProgress();
  
  // State for the current mission phase
  const [phase, setPhase] = useState<MissionPhase>(MissionPhase.BRIEFING);
  
  // State for mission results - expanded to include MoveMission stats
  const [missionResults, setMissionResults] = useState({
    success: false,
    score: 0,
    accuracy: 0,
    timeSpent: 0,
    movesUsed: 0,
    hintsUsed: 0
  });
  
  // State for tracking mission start time
  const [startTime, setStartTime] = useState<number | null>(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const completionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Get the mission data based on the ID
  const mission = missionId ? getMission(missionId) : null;
  const isE2ETestMode = typeof window !== 'undefined' && (window as { __E2E_TEST_MODE__?: boolean }).__E2E_TEST_MODE__ === true;

  // Handle navigation for sequence missions - MUST be called before any conditional returns
  useEffect(() => {
    if (mission && mission.type === 'sequence') {
      navigate(`/mission/sequence/${missionId}`, { replace: true });
    }
  }, [mission, missionId, navigate]);
  
  // Handle mission start
  const handleStartMission = () => {
    setPhase(MissionPhase.IN_PROGRESS);
    setStartTime(Date.now());
  };
  
  const transitionToResults = useCallback((completionData: {
    success: boolean;
    score: number;
    accuracy: number;
    timeSpent: number;
    movesUsed?: number;
    hintsUsed?: number;
  }) => {
    setPhase(MissionPhase.RESULTS);
    setMissionResults((prev: typeof missionResults) => ({
      ...prev,
      ...completionData,
      movesUsed: completionData.movesUsed ?? 0,
      hintsUsed: completionData.hintsUsed ?? 0,
    }));
  }, []);

  // Handle mission completion - now unified
  const handleMissionComplete = (completionData: {
    success: boolean;
    score: number;
    accuracy: number;
    timeSpent: number;
    movesUsed?: number;
    hintsUsed?: number;
  }) => {
    if (completionData.success && mission) {
      completeMission(mission.id, completionData);
    }

    if (completionData.success && SUCCESS_TRANSITION_DELAY_MS > 0) {
      setShowCompletionModal(true);
      completionTimerRef.current = setTimeout(() => {
        setShowCompletionModal(false);
        transitionToResults(completionData);
      }, SUCCESS_TRANSITION_DELAY_MS);
      return;
    }

    transitionToResults(completionData);
  };
  
  // Handle retry
  const handleRetry = () => {
    if (completionTimerRef.current) {
      clearTimeout(completionTimerRef.current);
      completionTimerRef.current = null;
    }
    setShowCompletionModal(false);
    setPhase(MissionPhase.BRIEFING);
  };
  
  // Handle continue to next mission or return to dashboard
  const handleContinue = () => {
    navigate('/dashboard');
    
    /**
     * TODO: Add logic to navigate to the next available mission
     * For example:
     * 
     * const availableMissions = getAvailableMissions(userProgress.completedMissions);
     * if (availableMissions.length > 0) {
     *   navigate(`/mission/${availableMissions[0].id}`);
     * } else {
     *   navigate('/dashboard');
     * }
     */
  };
  
  useEffect(() => {
    return () => {
      if (completionTimerRef.current) {
        clearTimeout(completionTimerRef.current);
      }
    };
  }, []);

  useScrollToTopOnChange(phase);

  // Loading state while context loads or mission is fetched
  if (isLoading) {
    return (
      <div className={styles.loading}>
        <p>Loading mission data...</p>
        {/* @TODO: Add asset: Loading spinner or animation */}
      </div>
    );
  }
  
  // Redirect if mission doesn't exist
  if (!mission) {
    return (
      <div className={styles.error}>
        <h2>Mission Not Found</h2>
        <p>The requested mission could not be found in our database.</p>
        <button 
          className={styles.returnButton} 
          onClick={() => navigate('/dashboard')}
        >
          Return to HQ
        </button>
      </div>
    );
  }

  // Redirect sequence missions to the dedicated sequence mission page
  if (mission.type === 'sequence') {
    return (
      <div className={styles.loading}>
        <p>Redirecting to sequence mission...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {showCompletionModal && (
        <div className={styles.completionModalOverlay} data-testid="mission-complete-modal">
          <div className={styles.completionModal}>
            <h2 className={styles.completionTitle}>Mission Accomplished</h2>
            <p className={styles.completionBody}>Debriefing report is being prepared...</p>
          </div>
        </div>
      )}
      {phase === MissionPhase.BRIEFING && mission && (
        <MissionBriefing
          mission={mission}
          onStartMission={handleStartMission}
        />
      )}
      
      {phase === MissionPhase.IN_PROGRESS && mission && (
        <div className={styles.missionInProgress}>
          {/* Render the appropriate mission component based on type */}
          {mission.type === 'coordinate' && (
            <MissionDebugProvider>
              <MissionDebugWrapper>
                <CoordinateChallenge
                  difficulty={mission.difficulty}
                  timeLimit={mission.timeLimit || 60}
                  targetScore={isE2ETestMode ? 1 : undefined}
                  missionId={mission.id}
                  testModeProps={isE2ETestMode ? {
                    disableTimer: true,
                    disableInterference: true,
                    cooldownMs: 0,
                    feedbackDurationMs: 50,
                  } : undefined}
                  onCompleteLegacy={(success, score, accuracy) => {
                    const timeSpent = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;
                    const completionData = createCoordinateCompletionData(success, score, accuracy, timeSpent);
                    handleMissionComplete(completionData);
                  }}
                  />
              </MissionDebugWrapper>
            </MissionDebugProvider>
          )}
          {/* Render MoveChallenge for 'move' type */}
          {mission.type === 'move' && mission.position && mission.targetMove && (
            <MissionDebugProvider>
              <MissionDebugWrapper>
                <MoveChallenge
                  missionId={mission.id}
                  position={mission.position}
                  targetMove={mission.targetMove}
                  targetScore={isE2ETestMode ? 1 : undefined}
                  timeLimit={mission.timeLimit ?? undefined}
                  difficulty={mission.difficulty}
                  maxMoves={mission.completionCriteria?.maxMoves}
                  missionTitle={mission.title}
                  pieces={mission.pieces}
                  testModeProps={isE2ETestMode ? {
                    disableTimer: true,
                    disableInterference: true,
                    feedbackDurationMs: 50,
                  } : undefined}
                  onCompleteLegacy={(success, score, accuracy, movesUsed = 0, hintsUsed = 0, timeSpent = 0) => {
                    const completionData = createMoveCompletionData(success, score, accuracy, timeSpent, hintsUsed, movesUsed);
                    handleMissionComplete(completionData);
                  }}
                  focus={mission.focus}
                />
              </MissionDebugWrapper>
            </MissionDebugProvider>
          )}
          {/* Placeholder for other/unimplemented mission types */}
          {mission && mission.type !== 'coordinate' && mission.type !== 'move' && (() => {
            // This block only runs if mission is defined and type is not coordinate or move.
            // Assign type to variable first to help TS.
            // const missionType = mission.type; // Removed due to persistent TS error
            return (
              <div className={styles.placeholder}>
                <h2>Mission Type Under Development</h2>
                <p>This mission type is currently under development.</p>
                <button
                  className={styles.returnButton}
                  onClick={() => handleMissionComplete({
                    success: false,
                    score: 0,
                    accuracy: 0,
                    timeSpent: 0,
                  })}
                >
                  Abort Mission
                </button>
              </div>
            );
          })()}
        </div>
      )}
      
      {phase === MissionPhase.RESULTS && mission && (
        <MissionResults
          mission={mission}
          results={missionResults}
          onRetry={handleRetry}
          onContinue={handleContinue}
        />
      )}
    </div>
  );
};

export default MissionPage; 
